import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';
import MLIcon from '../MLIcon/MLIcon';
import { useStyles } from './styles';

const COPIED_RESET_DELAY_MS = 2000;
const COPY_LABEL = 'Copy email to clipboard';
const COPIED_LABEL = 'Copied!';

function EmailTooltip({ children, email }) {
  const classes = useStyles();
  const [isCopied, setIsCopied] = useState(false);
  const resetTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(resetTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setIsCopied(false);
    clearTimeout(resetTimeoutRef.current);
  }, [email]);

  const handleCopyToClipboard = async () => {
    if (!navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(email);
      if (!isMountedRef.current) return;
      setIsCopied(true);
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = setTimeout(
        () => setIsCopied(false),
        COPIED_RESET_DELAY_MS
      );
    } catch {
      // Clipboard write can fail (permissions or insecure context); non-fatal.
    }
  };

  const renderTitle = () => (
    <div className={classes.copyEmailTooltip}>
      <button
        type="button"
        className={classes.copyEmailButton}
        style={{ minWidth: `calc(${COPY_LABEL.length}ch + 56px)` }}
        onClick={handleCopyToClipboard}
      >
        <MLIcon
          type={isCopied ? 'check' : 'item_copy'}
          role="img"
          className={classes.tooltipIcon}
        />
        <span className={classes.label}>
          {isCopied ? COPIED_LABEL : COPY_LABEL}
        </span>
      </button>
    </div>
  );

  return (
    <Tooltip
      classes={{
        tooltipPlacementBottom: classes.tooltipPlacementBottom,
        tooltip: classes.tooltip,
        arrow: classes.arrow,
      }}
      arrow
      interactive
      placement="bottom-start"
      title={renderTitle()}
    >
      {children}
    </Tooltip>
  );
}

EmailTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  email: PropTypes.string.isRequired,
};

export default EmailTooltip;
